import React from 'react';
import { COLORS, TYPOGRAPHY, SHADOWS } from '../../../styles/UISystem';
import { TextLarge } from '../Typography';

/**
 * BottomSheet Component
 *
 * A bottom sheet overlay that appears from the bottom of the screen
 * with rounded top corners, white background, and customizable content.
 * Includes a darkened overlay for the rest of the page.
 *
 * @param {React.ReactNode} children - Content to display inside the bottom sheet
 * @param {function} onClose - Function to call when the bottom sheet is closed
 * @param {object} style - Additional style overrides
 * @param {boolean} showOverlay - Whether to show the darkened overlay (default: true)
 * @param {object} overlayStyle - Additional style overrides for the overlay
 */
export const BottomSheet = ({
  children,
  style,
  onClose,
  showOverlay = true,
  overlayStyle = {},
  ...props
}) => {
  return (
    <>
      {showOverlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 999,
            ...overlayStyle,
          }}
          onClick={onClose}
        />
      )}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          backgroundColor: COLORS.WHITE,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          padding: '24px',
          zIndex: 1000,
          ...style,
        }}
        {...props}
      >
        {children}
      </div>
    </>
  );
};

/**
 * BottomSheetExample Component
 *
 * Example usage of the BottomSheet component.
 */
export const BottomSheetExample = () => {
  const [showStandardSheet, setShowStandardSheet] = React.useState(false);
  const [showOverlaySheet, setShowOverlaySheet] = React.useState(false);

  return (
    <div>
      {/* Standard Bottom Sheet Example */}
      <div
        style={{
          position: 'relative',
          height: '300px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '32px',
        }}
      >
        <div
          style={{
            height: '100%',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: COLORS.MEDIUM_GRAY,
          }}
        >
          <div style={{ marginBottom: '16px' }}>Standard Bottom Sheet</div>
          <button
            onClick={() => setShowStandardSheet(true)}
            style={{
              backgroundColor: COLORS.PRIMARY_BLUE,
              color: COLORS.WHITE,
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Show Standard Sheet
          </button>
        </div>

        {showStandardSheet && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              backgroundColor: COLORS.WHITE,
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              boxShadow: SHADOWS.TIER3,
              padding: '24px',
            }}
          >
            <TextLarge style={{ marginBottom: '16px' }}>Standard Bottom Sheet</TextLarge>
            <div style={{ ...TYPOGRAPHY.BODY_REGULAR, marginBottom: '24px' }}>
              This is a bottom sheet that appears from the bottom of the screen with rounded top
              corners. It uses a drop shadow for visual separation.
            </div>
            <button
              onClick={() => setShowStandardSheet(false)}
              style={{
                backgroundColor: COLORS.MEDIUM_BLUE,
                color: COLORS.LIGHT_GRAY,
                border: 'none',
                borderRadius: '30px',
                padding: '12px 24px',
                width: '100%',
                ...TYPOGRAPHY.BUTTON_REGULAR,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Bottom Sheet with Overlay Example */}
      <div
        style={{
          position: 'relative',
          height: '300px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: COLORS.MEDIUM_GRAY,
          }}
        >
          <div style={{ marginBottom: '16px' }}>Bottom Sheet with Darkened Page Overlay</div>
          <button
            onClick={() => setShowOverlaySheet(true)}
            style={{
              backgroundColor: COLORS.PRIMARY,
              color: COLORS.WHITE,
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Show Sheet with Overlay
          </button>
        </div>
      </div>

      {/* Actual Bottom Sheet with Overlay */}
      {showOverlaySheet && (
        <BottomSheet onClose={() => setShowOverlaySheet(false)} showOverlay={true}>
          <TextLarge style={{ marginBottom: '16px' }}>Bottom Sheet</TextLarge>
          <div style={{ ...TYPOGRAPHY.BODY_REGULAR, marginBottom: '24px' }}>
            This white bottom sheet appears with a darkened overlay behind it. The overlay covers
            the entire page and clicking it dismisses the sheet.
          </div>
          <button
            onClick={() => setShowOverlaySheet(false)}
            style={{
              backgroundColor: COLORS.PRIMARY_BLUE,
              color: COLORS.WHITE,
              border: 'none',
              borderRadius: '30px',
              padding: '12px 24px',
              width: '100%',
              ...TYPOGRAPHY.BUTTON_REGULAR,
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </BottomSheet>
      )}
    </div>
  );
};
